import { useCallback, useState } from 'react';
import type { PregnancyWeek, PregnancyWeekDraft } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAsync, useDebounced } from '../hooks/use-async';
import { useIsMobile } from '../hooks/use-is-mobile';
import { useRealtimeVersion } from '../providers/realtime-context';
import {
  ConfirmModal,
  EmptyState,
  ErrorState,
  Modal,
  RefreshButton,
  SearchInput,
  TableSkeleton,
  TextAreaField,
  TextField,
} from '../components/ui';
import { PhotoGalleryUpload } from '../components/photo-gallery-upload';

const EMPTY_DRAFT: PregnancyWeekDraft = {
  title: '',
  body: '',
  fruit: '',
  tailleCm: null,
  poidsG: null,
  moisIndex: null,
  anecdote: null,
  tipsToDo: [],
  tipsToAvoid: [],
  imageKey: null,
  illustrationSvg: null,
  illustrationScalePct: null,
};

function toDraft(w: PregnancyWeek): PregnancyWeekDraft {
  return {
    title: w.title,
    body: w.body,
    fruit: w.fruit,
    tailleCm: w.tailleCm,
    poidsG: w.poidsG,
    moisIndex: w.moisIndex,
    anecdote: w.anecdote,
    tipsToDo: w.tipsToDo,
    tipsToAvoid: w.tipsToAvoid,
    imageKey: w.imageKey,
    illustrationSvg: w.illustrationSvg,
    illustrationScalePct: w.illustrationScalePct,
  };
}

function TipsList({
  label,
  items,
  onChange,
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="bbm-label" style={{ marginBottom: 6 }}>{label}</div>
      {items.map((tip, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input
            className="bbm-field"
            style={{ flex: 1 }}
            value={tip}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <button
            type="button"
            className="bbm-btn bbm-btn--danger"
            style={{ flexShrink: 0 }}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <button type="button" className="bbm-btn" onClick={() => onChange([...items, ''])}>
        + Ajouter
      </button>
    </div>
  );
}

function SvgPreview({ svg }: { svg: string }) {
  if (!svg.trim()) return null;
  return (
    <div
      style={{
        width: 80,
        height: 80,
        borderRadius: 10,
        border: '1px solid #DDE2E7',
        background: '#F7F9FB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}
      // SVG content comes from admin-controlled data, not user input
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export function PregnancyPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);

  const [editing, setEditing] = useState<PregnancyWeek | null>(null);
  const [draft, setDraft] = useState<PregnancyWeekDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const isMobile = useIsMobile();
  const vWeeks = useRealtimeVersion('weeks');
  const resource = useAsync(
    () => container.pregnancy.list.execute(debouncedSearch),
    [container, debouncedSearch, vWeeks],
  );

  const weeks = resource.data ?? [];

  const openEditor = useCallback((week: PregnancyWeek) => {
    setEditing(week);
    setDraft(toDraft(week));
  }, []);

  const save = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await container.pregnancy.save.execute({ week: editing.week, draft });
      toast.notify(`Semaine ${editing.week} enregistrée`);
      setEditing(null);
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editing, draft, toast, resource]);

  const validate = useCallback(
    async (week: number) => {
      try {
        await container.pregnancy.validate.execute(week);
        toast.notify(`Semaine ${week} validée`);
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, resource],
  );

  const setNum = (field: keyof PregnancyWeekDraft, raw: string) => {
    const n = raw === '' ? null : Number(raw);
    setDraft((d) => ({ ...d, [field]: n === null || isNaN(n) ? null : n }));
  };

  const hasContent = (w: PregnancyWeek) => !!(w.title || w.fruit || w.body);

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <SearchInput
            value={search}
            width={280}
            placeholder="Rechercher SA ou fruit…"
            onChange={setSearch}
          />
        </div>
        <div className="bbm-toolbar__end">
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {resource.loading ? <TableSkeleton /> : null}
      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={resource.error.message}
          onRetry={resource.reload}
        />
      ) : null}
      {!resource.loading && !resource.error && weeks.length === 0 ? (
        <EmptyState title="Aucun résultat" body="Aucune semaine ne correspond à cette recherche." />
      ) : null}

      {weeks.length > 0 ? (
        isMobile ? (
          <div className="bbm-card-stack">
            {weeks.map((item) => (
              <div key={item.week} className="bbm-mobile-card" style={{ opacity: hasContent(item) ? 1 : 0.45 }}>
                <div className="bbm-mobile-card__head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }} />
                    ) : item.illustrationSvg ? (
                      <div
                        aria-hidden="true"
                        style={{ width: 36, height: 36, borderRadius: 7, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EEF1F4' }}
                        // SVG content comes from admin-controlled data
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: item.illustrationSvg }}
                      />
                    ) : null}
                    <div>
                      <div className="bbm-mobile-card__title">SA {item.week}</div>
                      <div className="bbm-mobile-card__sub">{item.fruit || 'Non renseigné'}</div>
                    </div>
                  </div>
                </div>
                <div className="bbm-mobile-card__actions">
                  {hasContent(item) ? (
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--success"
                      style={item.validated ? { background: '#5CA989', color: '#fff', borderColor: 'transparent' } : undefined}
                      onClick={() => setPending({
                        title: 'Valider cette semaine ?',
                        body: 'Les données de cette semaine seront enregistrées.',
                        confirmLabel: 'Valider',
                        fn: () => void validate(item.week),
                      })}
                    >{item.validated ? '✓ Validé' : 'Valider'}</button>
                  ) : null}
                  <button type="button" className="bbm-btn" onClick={() => openEditor(item)}>
                    {hasContent(item) ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bbm-panel">
            <div className="bbm-row bbm-row--head">
              <div style={{ width: 60 }}>SA</div>
              <div style={{ width: 46 }}>Photo</div>
              <div style={{ width: 140 }}>Fruit</div>
              <div style={{ width: 70 }}>Taille</div>
              <div style={{ width: 70 }}>Poids</div>
              <div style={{ marginLeft: 'auto' }}>Actions</div>
            </div>
            {weeks.map((item) => (
              <div
                key={item.week}
                className="bbm-row"
                style={{
                  padding: '8px 20px',
                  opacity: hasContent(item) ? 1 : 0.45,
                }}
              >
                <div className="bbm-cell-strong" style={{ width: 60 }}>
                  SA {item.week}
                </div>
                <div
                  aria-hidden="true"
                  style={{
                    width: 46,
                    height: 34,
                    borderRadius: 7,
                    background: item.imageUrl
                      ? `url(${item.imageUrl}) center/cover`
                      : item.illustrationSvg
                      ? undefined
                      : '#EEF1F4',
                    flexShrink: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  dangerouslySetInnerHTML={
                    !item.imageUrl && item.illustrationSvg
                      ? { __html: item.illustrationSvg }
                      : undefined
                  }
                />
                <div className="bbm-cell bbm-truncate" style={{ width: 140 }}>
                  {item.fruit || <span style={{ color: '#B0B8C3' }}>—</span>}
                </div>
                <div className="bbm-cell" style={{ width: 70, color: '#8A95A3', fontSize: 13 }}>
                  {item.tailleCm != null ? `${item.tailleCm} cm` : '—'}
                </div>
                <div className="bbm-cell" style={{ width: 70, color: '#8A95A3', fontSize: 13 }}>
                  {item.poidsG != null ? `${item.poidsG} g` : '—'}
                </div>
                <div className="bbm-row__actions">
                  {hasContent(item) ? (
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--success"
                      style={
                        item.validated
                          ? { background: '#5CA989', color: '#fff', borderColor: 'transparent' }
                          : undefined
                      }
                      onClick={() => setPending({
                        title: 'Valider cette semaine ?',
                        body: 'Les données de cette semaine seront enregistrées.',
                        confirmLabel: 'Valider',
                        fn: () => void validate(item.week),
                      })}
                    >
                      {item.validated ? '✓ Validé' : 'Valider'}
                    </button>
                  ) : null}
                  <button type="button" className="bbm-btn" onClick={() => openEditor(item)}>
                    {hasContent(item) ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
      {editing ? (
        <Modal
          title={`SA ${editing.week}${editing.title ? ` — ${editing.title}` : ''}`}
          onClose={() => setEditing(null)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <TextField
              label="Titre"
              value={draft.title}
              onChange={(v) => setDraft((d) => ({ ...d, title: v }))}
            />
            <TextField
              label="Fruit"
              value={draft.fruit}
              onChange={(v) => setDraft((d) => ({ ...d, fruit: v }))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <label className="bbm-label">Mois (1–9)</label>
              <input
                type="number"
                className="bbm-field"
                min={1}
                max={9}
                value={draft.moisIndex ?? ''}
                onChange={(e) => setNum('moisIndex', e.target.value)}
              />
            </div>
            <div>
              <label className="bbm-label">Taille (cm)</label>
              <input
                type="number"
                className="bbm-field"
                step="0.1"
                min={0}
                value={draft.tailleCm ?? ''}
                onChange={(e) => setNum('tailleCm', e.target.value)}
              />
            </div>
            <div>
              <label className="bbm-label">Poids (g)</label>
              <input
                type="number"
                className="bbm-field"
                min={0}
                value={draft.poidsG ?? ''}
                onChange={(e) => setNum('poidsG', e.target.value)}
              />
            </div>
          </div>

          <TextAreaField
            label="Corps du contenu"
            rows={4}
            value={draft.body}
            onChange={(v) => setDraft((d) => ({ ...d, body: v }))}
          />

          <TextAreaField
            label='Anecdote ("Le savais-tu ?")'
            rows={2}
            value={draft.anecdote ?? ''}
            onChange={(v) => setDraft((d) => ({ ...d, anecdote: v || null }))}
          />

          <TipsList
            label="Conseils à faire"
            items={draft.tipsToDo}
            onChange={(next) => setDraft((d) => ({ ...d, tipsToDo: next }))}
          />

          <TipsList
            label="À éviter"
            items={draft.tipsToAvoid}
            onChange={(next) => setDraft((d) => ({ ...d, tipsToAvoid: next }))}
          />

          <div>
            <span className="bbm-label">Photo</span>
            <PhotoGalleryUpload
              key={editing.week}
              initialKeys={draft.imageKey ? [draft.imageKey] : []}
              initialUrls={editing.imageUrl ? [editing.imageUrl] : []}
              purpose="pregnancy-image"
              onChange={(keys) => setDraft((d) => ({ ...d, imageKey: keys[0] ?? null }))}
              onRequestUpload={container.media.requestUpload}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div>
              <label className="bbm-label">Échelle illustration (%)</label>
              <input
                type="number"
                className="bbm-field"
                min={1}
                max={100}
                value={draft.illustrationScalePct ?? ''}
                onChange={(e) => setNum('illustrationScalePct', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <TextAreaField
                label="SVG illustration (code brut)"
                rows={5}
                value={draft.illustrationSvg ?? ''}
                onChange={(v) => setDraft((d) => ({ ...d, illustrationSvg: v || null }))}
              />
            </div>
            {draft.illustrationSvg ? (
              <div style={{ paddingTop: 22 }}>
                <SvgPreview svg={draft.illustrationSvg} />
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
